import React from "react";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const cspUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Impact of Computing",
  examWeight: "21–26%",
  focus: "Computing innovations influence society, economy, and culture. Programmers must understand potential impacts, legal/ethical concerns, and safe computing practices.",
  sections: [
    {
      id: "topic-5-1",
      title: "Topic 5.1: Beneficial and Harmful Effects",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 The Nature of Computing Innovations</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A <strong>Computing Innovation</strong> is an innovation that includes a computer or program code as an integral part of its function.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Function vs. Impact</h4>
                <p className="text-sm text-slate-700 mt-2">
                  The <strong>function</strong> is what the program is designed to do (e.g., GPS directions). The <strong>impact</strong> is the consequence on society (e.g., fuel reduction vs. residential traffic).
                </p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Unanticipated Effects</h4>
                <p className="text-sm text-slate-700 mt-2">
                  Not every effect is planned. The World Wide Web was meant for scientific exchange, but its expansion into e-commerce and social media was unanticipated.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Dual Nature of Impacts</h3>
            <div className="space-y-3">
              <p className="text-slate-700 leading-relaxed">
                A single effect can be viewed as both beneficial and harmful depending on <strong>perspective</strong>.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2">Example: Targeted Advertising</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                  <li><span className="text-green-700 font-semibold">Benefit:</span> Users find products they want; businesses reach ideal customers efficiently.</li>
                  <li><span className="text-red-700 font-semibold">Harm:</span> Privacy invasion; can be used to manipulate voters or exclude groups from housing/job ads.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📡 Impact Beyond Purpose</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Programmers cannot foresee all ways their code will be used once it is shared rapidly with a massive user base.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Machine Learning</h4>
                <p className="text-xs text-slate-700 mt-1">Intended for medicine/science, but can be used to discriminate in loan approvals or hiring.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">Social Media</h4>
                <p className="text-xs text-slate-700 mt-1">Intended to connect friends, but used to spread misinformation or cyberbully.</p>
              </div>
            </div>
            <ImportantNote>
              Responsible programmers try to consider unintended uses, but they cannot predict everything.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: Digital Divide",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 The Connectivity Gap</h3>
            <KeyTerm 
              term="Digital Divide" 
              definition="The gap between those who have access to computing devices and the Internet and those who do not."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Factors Contributing to the Divide</h3>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">💰 Socioeconomic</h4>
                <p className="text-xs text-slate-700 mt-1">Ability to afford hardware and high-speed broadband subscriptions.</p>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">📍 Geographic</h4>
                <p className="text-xs text-slate-700 mt-1">Rural areas often lack infrastructure (fiber/cell towers) present in urban areas.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">👥 Demographic</h4>
                <p className="text-xs text-slate-700 mt-1">Age, disability status, race, and education level influence literacy and access.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Impact and Solutions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The divide raises issues of <strong>equity, access, and influence</strong>. It limits the diversity of voices and prevents full participation in the modern economy.
            </p>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-bold text-yellow-900 mb-2">Addressing the Divide</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li><strong>Individuals:</strong> Community organizing and resource sharing.</li>
                <li><strong>Organizations:</strong> Non-profits providing devices or training.</li>
                <li><strong>Governments:</strong> Subsidizing infrastructure or internet costs.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: Computing Bias",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤖 Origins of Bias</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Innovations reflect existing human biases. Bias can be embedded at <strong>all levels</strong> of development (concept, design, coding, testing).
            </p>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Biased Algorithms</h4>
                <p className="text-sm text-slate-700 mt-1">The programmer creates code that explicitly (often unintentionally) favors one group over another.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Biased Data</h4>
                <p className="text-sm text-slate-700 mt-1">The most common source. If training data is not representative, the innovation will be biased (e.g., facial recognition trained mostly on white men failing for women of color).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ Mitigation</h3>
            <div className="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400">
              <h4 className="font-bold text-emerald-900">Diversify Development Teams</h4>
              <p className="text-sm text-slate-700 mt-1">Including people of different races, genders, and backgrounds helps identify blind spots and biases before the product is released.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: Crowdsourcing",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Collaborative Models</h3>
            <KeyTerm 
              term="Crowdsourcing" 
              definition="Obtaining input or information from a large number of people via the Internet."
            />
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
              <h4 className="font-bold text-blue-900">Citizen Science</h4>
              <p className="text-sm text-slate-700 mt-1">Non-scientists contribute data to research. This allows for massive data collection and problem-solving impossible for scientists alone (e.g., classifying galaxy images).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Benefits of the Crowd</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-900">Scale</h5>
                <p className="text-xs text-slate-700 mt-1">Access to massive amounts of data and labor.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-900">Innovation</h5>
                <p className="text-xs text-slate-700 mt-1">Connects projects with funding (Kickstarter, GoFundMe).</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-900">Problem Solving</h5>
                <p className="text-xs text-slate-700 mt-1">Distributed individuals solving complex puzzles (e.g., Foldit protein structures).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-5",
      title: "Topic 5.5: Legal and Ethical Concerns",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Intellectual Property (IP)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Material created on a computer is the <strong>IP</strong> of the creator. Ease of copying digital files raises concerns about ownership.
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Plagiarism" 
                definition="Using material created by someone else without permission AND presenting it as your own."
              />
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                  <h4 className="font-bold text-pink-900 text-sm">Creative Commons</h4>
                  <p className="text-xs text-slate-700 mt-1">A public license where the author tells others exactly how they can share or remix the work.</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <h4 className="font-bold text-orange-900 text-sm">Open Source</h4>
                  <p className="text-xs text-slate-700 mt-1">Software code made freely available; may be redistributed and modified.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="font-bold text-red-900 text-sm">Open Access</h4>
                  <p className="text-xs text-slate-700 mt-1">Online research output free of restrictions (no paywalls).</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Ethical Use and Piracy</h3>
            <div className="space-y-3">
              <ImportantNote>
                Even when using Open Source or Creative Commons material, <strong>you must cite the source</strong>. Attribution is an ethical requirement.
              </ImportantNote>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Key Ethical Issues</h4>
                <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-slate-700">
                  <li><strong>Digital Piracy:</strong> Illegal downloading of copyrighted material.</li>
                  <li><strong>Harm:</strong> Cyberbullying and stalking facilitated by computing.</li>
                  <li><strong>Access:</strong> The digital divide itself is a question of equal opportunity.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-6",
      title: "Topic 5.6: Safe Computing",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔐 PII and Privacy</h3>
            <KeyTerm 
              term="PII" 
              definition="Personally Identifiable Information: Any info that identifies or relates to an individual (SSN, medical info, biometrics)."
            />
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 my-4">
              <h4 className="font-bold text-red-900">Aggregation Risk</h4>
              <p className="text-sm text-slate-700 mt-1">Combining disparate data (geolocation + cookies + history) can create a detailed profile of a person, even if individual points seem harmless.</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-900">Data Tracking</h4>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-slate-700">
                <li><strong>Permanence:</strong> Online info is essentially impossible to delete entirely.</li>
                <li><strong>Search Engines:</strong> Record history for marketing.</li>
                <li><strong>Devices:</strong> Track geolocation (location and duration of stays).</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ Protection Measures</h3>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 mb-4">
              <h4 className="font-bold text-indigo-900">Multifactor Authentication (MFA)</h4>
              <p className="text-sm text-slate-700 mt-1 mb-3">Requires at least <strong>two different</strong> categories:</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white p-2 rounded border border-indigo-100 text-center">
                  <p className="text-xs font-bold text-indigo-800">Knowledge</p>
                  <p className="text-[10px] text-slate-500">Password/PIN</p>
                </div>
                <div className="bg-white p-2 rounded border border-indigo-100 text-center">
                  <p className="text-xs font-bold text-indigo-800">Possession</p>
                  <p className="text-[10px] text-slate-500">Phone/Token</p>
                </div>
                <div className="bg-white p-2 rounded border border-indigo-100 text-center">
                  <p className="text-xs font-bold text-indigo-800">Inherence</p>
                  <p className="text-[10px] text-slate-500">Fingerprint/Face</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Symmetric Key Encryption</h4>
                <p className="text-sm text-slate-700 mt-1">Uses <strong>one</strong> key for both encryption and decryption. Faster but riskier key exchange.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Public Key (Asymmetric) Encryption</h4>
                <p className="text-sm text-slate-700 mt-1">Uses <strong>two</strong> keys. Public key (to encrypt) is shared; Private key (to decrypt) is secret.</p>
              </div>
              <KeyTerm 
                term="Certificate Authorities (CAs)" 
                definition="Entities that issue digital certificates verifying that public keys belong to the entity they claim—the basis of the internet 'Trust Model'."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Cyber Threats and Defense</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-bold text-red-900 text-sm">Phishing</h5>
                <p className="text-xs text-slate-700 mt-1">Tricking users into providing personal info via deceptive emails.</p>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-bold text-red-900 text-sm">Keylogging</h5>
                <p className="text-xs text-slate-700 mt-1">Software that records keystrokes to steal passwords.</p>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-bold text-red-900 text-sm">Malware & Viruses</h5>
                <p className="text-xs text-slate-700 mt-1">Malicious programs intended to damage systems or gain unauthorized access.</p>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-bold text-red-900 text-sm">Rogue Access Point</h5>
                <p className="text-xs text-slate-700 mt-1">Unauthorized WiFi hotspots set up to intercept data.</p>
              </div>
            </div>
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <h4 className="font-bold text-emerald-900 mb-2">Defense Strategies</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li><strong>Software Updates:</strong> Fix design flaws (vulnerabilities) hackers exploit.</li>
                <li><strong>Antivirus:</strong> Scans and removes known malicious code.</li>
                <li><strong>User Behavior:</strong> Check URLs, avoid untrusted sites, review permissions.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default cspUnit5Notes;